import type { PublicWebPushOptInConfig } from './types/sdk.types';
import type { PushStatus } from './push/PushManager';

/**
 * Minimal structural page context — the controller only needs the page type.
 * Decoupled from the concrete PageContext types (sdk.types vs adapters) so it
 * accepts either without a cast.
 */
export interface WebPushPageContext {
  pageType?: string;
}

export interface WebPushOptInController {
  schedule(config: PublicWebPushOptInConfig | undefined, pageContext: WebPushPageContext | null): void;
  destroy(): void;
}

interface ControllerOptions {
  storagePrefix: string;
  subscribeToPush: () => Promise<PushStatus>;
}

const ROOT_ATTR = 'data-hamla-web-push-opt-in';

function getNotificationPermission(): NotificationPermission | 'default' {
  const notification = (window as any).Notification;
  return notification?.permission ?? 'default';
}

function isIosBrowserTab() {
  const userAgent = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(userAgent) || (userAgent.includes('Mac') && 'ontouchend' in document);
  const isStandalone = window.matchMedia?.('(display-mode: standalone)').matches || (navigator as any).standalone === true;
  return isIos && !isStandalone;
}

export function canShowWebPushOptIn(): boolean {
  if (typeof window === 'undefined' || typeof document === 'undefined') return false;
  if (isIosBrowserTab()) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!('Notification' in window)) return false;
  const permission = getNotificationPermission();
  return permission !== 'granted' && permission !== 'denied';
}

function storageKey(prefix: string, key: string) {
  return `hamla:web-push-opt-in:${prefix}:${key}`;
}

function isDismissed(prefix: string, cooldownDays: number) {
  const raw = localStorage.getItem(storageKey(prefix, 'dismissedAt'));
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!Number.isFinite(dismissedAt)) return false;
  const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt < cooldownMs;
}

function markDismissed(prefix: string) {
  localStorage.setItem(storageKey(prefix, 'dismissedAt'), String(Date.now()));
}

function markSubscribed(prefix: string) {
  localStorage.setItem(storageKey(prefix, 'subscribed'), '1');
}

function isSubscribed(prefix: string) {
  return localStorage.getItem(storageKey(prefix, 'subscribed')) === '1';
}

function shownThisSession(prefix: string) {
  return sessionStorage.getItem(storageKey(prefix, 'shown')) === '1';
}

function markShown(prefix: string) {
  sessionStorage.setItem(storageKey(prefix, 'shown'), '1');
}

function removeExisting() {
  document.querySelectorAll(`[${ROOT_ATTR}]`).forEach((node) => node.remove());
}

function createLogo(config: PublicWebPushOptInConfig) {
  if (config.branding.logoUrl) {
    const img = document.createElement('img');
    img.src = config.branding.logoUrl;
    img.alt = '';
    img.style.cssText = 'width:36px;height:36px;border-radius:8px;object-fit:cover;flex:0 0 auto;';
    return img;
  }
  const fallback = document.createElement('div');
  fallback.textContent = (config.branding.storeName || 'H').charAt(0).toUpperCase();
  fallback.style.cssText = 'width:36px;height:36px;border-radius:8px;display:flex;align-items:center;justify-content:center;background:#f3f4f6;color:#111827;font:600 14px system-ui;';
  return fallback;
}

function renderCard(config: PublicWebPushOptInConfig, options: ControllerOptions) {
  removeExisting();

  const root = document.createElement('div');
  root.setAttribute(ROOT_ATTR, 'true');
  root.dir = 'auto';
  root.style.cssText = [
    'position:fixed',
    config.style === 'floating_bell' ? 'right:18px;bottom:18px;max-width:360px' : 'left:50%;bottom:18px;transform:translateX(-50%);width:min(720px,calc(100vw - 24px))',
    'z-index:2147483000',
    'box-sizing:border-box',
    'font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  ].join(';');

  const card = document.createElement('div');
  card.style.cssText = 'display:flex;gap:12px;align-items:flex-start;background:#fff;color:#111827;border:1px solid rgba(17,24,39,.12);box-shadow:0 16px 48px rgba(17,24,39,.18);border-radius:12px;padding:14px;';
  card.appendChild(createLogo(config));

  const content = document.createElement('div');
  content.style.cssText = 'min-width:0;flex:1;';
  const title = document.createElement('div');
  title.textContent = config.message.title;
  title.style.cssText = 'font-weight:700;font-size:14px;line-height:1.25;margin-bottom:4px;';
  const body = document.createElement('div');
  body.textContent = config.message.body;
  body.style.cssText = 'font-size:13px;line-height:1.35;color:#4b5563;margin-bottom:12px;';
  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;flex-wrap:wrap;';

  const secondary = document.createElement('button');
  secondary.type = 'button';
  secondary.setAttribute('data-hamla-web-push-dismiss', 'true');
  secondary.textContent = config.message.secondaryCta;
  secondary.style.cssText = 'border:0;background:transparent;color:#4b5563;font:600 13px system-ui;padding:8px 10px;cursor:pointer;';
  secondary.onclick = () => {
    markDismissed(options.storagePrefix);
    removeExisting();
  };

  const primary = document.createElement('button');
  primary.type = 'button';
  primary.setAttribute('data-hamla-web-push-enable', 'true');
  primary.textContent = config.message.primaryCta;
  primary.style.cssText = `border:0;border-radius:8px;background:${config.branding.primaryColor};color:#fff;font:700 13px system-ui;padding:8px 12px;cursor:pointer;`;
  primary.onclick = async () => {
    primary.disabled = true;
    const result = await options.subscribeToPush();
    if (result.subscribed || result.permission === 'granted') {
      markSubscribed(options.storagePrefix);
    }
    if (result.permission === 'denied') {
      markDismissed(options.storagePrefix);
    }
    removeExisting();
  };

  actions.append(secondary, primary);
  content.append(title, body, actions);
  card.appendChild(content);
  root.appendChild(card);
  document.body.appendChild(root);
}

export function createWebPushOptInController(options: ControllerOptions): WebPushOptInController {
  let timer: number | null = null;

  return {
    schedule(config, pageContext) {
      if (timer !== null) window.clearTimeout(timer);
      if (!config?.enabled) return;
      // Server already knows this visitor subscribed (authoritative for the
      // cross-origin popup flow, where browser checks below are blind).
      if (config.alreadySubscribed) return;
      if (!canShowWebPushOptIn()) return;
      if (isSubscribed(options.storagePrefix)) return;
      if (shownThisSession(options.storagePrefix)) return;
      if (isDismissed(options.storagePrefix, config.display.dismissCooldownDays)) return;
      const pageType = pageContext?.pageType;
      if (!pageType || !config.display.pageTypes.includes(pageType as any)) return;

      timer = window.setTimeout(() => {
        markShown(options.storagePrefix);
        renderCard(config, options);
      }, config.display.showAfterSeconds * 1000);
    },
    destroy() {
      if (timer !== null) window.clearTimeout(timer);
      timer = null;
      removeExisting();
    },
  };
}
